import type { LoanType } from '@apply/constants/src/loans/LoanType';
import {
  BRIDGE_RETAINED_CAP,
  EXPERIENCE_LIMIT_TABLE,
  LOAN_BUCKETS,
  LOAN_TYPE_BUCKET,
  MAX_SURPLUS_PER_CATEGORY,
  PRODUCT_MODE,
  REPAYMENT_CREDIT_RATE,
  SURPLUS_LIMIT,
  UNBUCKETED,
} from '../constants';
import type {
  BllMode,
  BorrowerInput,
  BorrowerResult,
  BridgeAllocation,
  BucketResult,
  CategoryBreakdown,
  CategoryResult,
  ExperienceLimitTier,
  LendingCategory,
  LoanBucket,
  LoanGroup,
  LoanProduct,
  LoanRecord,
  SummaryInput,
  SummaryResult,
  SummaryTotals,
} from '../types';

/** Round to 2 decimals (matches the workbook's ROUND on the Summary totals; also
 *  strips floating-point noise from summed money). */
function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ── Experience & limit ────────────────────────────────────────────────────────

/**
 * `B17`/`B19` VLOOKUP against `Data!O21:R221`.
 *
 * Returns the highest band whose `minExperience` is ≤ `score`. Scores below the
 * first band fall back to the first band (the workbook's table starts at 0).
 */
export function lookupExperienceTier(
  score: number,
  table: readonly ExperienceLimitTier[] = EXPERIENCE_LIMIT_TABLE,
): ExperienceLimitTier {
  let match = table[0];
  for (const tier of table) {
    if (score >= tier.minExperience) {
      match = tier;
    } else {
      break;
    }
  }
  return match;
}

/** Resolve the fee-treatment mode for a product (`CORE` vs `SHEPHERDS`). */
export function resolveMode(product: LoanProduct): BllMode {
  return PRODUCT_MODE[product];
}

/**
 * Experience score box (`Q2:Q5`).
 *
 *   Q4 (repaymentCount) = number of repaid loans (`isOutstanding === false`)
 *   Q5 (score)          = builtSince2011 + gcBonus + repaymentCount
 */
export function computeExperience(input: BorrowerInput): {
  repaymentCount: number;
  score: number;
} {
  const repaymentCount = input.loans.filter((loan) => !loan.isOutstanding).length;
  return {
    repaymentCount,
    score: input.builtSince2011 + input.gcBonus + repaymentCount,
  };
}

/**
 * `B18`: 50% of total repaid value.
 *
 * Per repaid loan (section II) the "Amount Repaid" is the principal advanced and
 * returned to BFI plus interest paid: `-principalPaymentReceived + ytdInterestPaid`
 * (principalPaymentReceived is stored negative). This follows the workbook's cover
 * note — "repayments include all interest & fees repaid by borrower" — and the
 * corrected `Borrower N` tabs (e.g. Borrower 4: `D = -advanced + interest`).
 *
 * KNOWN WORKBOOK INCONSISTENCY: a few tabs (e.g. Borrower 2) have a stale formula
 * that omits the interest term, so their Summary cell under-credits the limit. This
 * library implements the documented (interest-inclusive) rule.
 *
 * Outstanding loans contribute 0.
 */
export function computeRepaymentCredit(loans: LoanRecord[]): number {
  const totalRepaid = loans
    .filter((loan) => !loan.isOutstanding)
    .reduce((sum, loan) => sum + (-loan.principalPaymentReceived + loan.ytdInterestPaid), 0);
  return REPAYMENT_CREDIT_RATE * totalRepaid;
}

// ── Category breakdown ────────────────────────────────────────────────────────
//
// Everything below splits a borrower's ledger the way the UI presents it. None of it
// comes from the workbook — see the "Category breakdown" block in `../constants` for
// which pieces are assumptions awaiting business confirmation.

/** Sort one loan into its detail-table bucket. Unknown/absent types fall back. */
export function resolveBucket(loanType?: LoanType | null): LoanBucket {
  return loanType == null ? UNBUCKETED : (LOAN_TYPE_BUCKET[loanType] ?? UNBUCKETED);
}

/** Aggregate a set of loans into every measure the UI might ask for. */
export function aggregateLoans(loans: LoanRecord[], mode: BllMode): LoanGroup {
  const commitment = sumBy(loans, (loan) => loan.commitmentAmount);
  const financedFees = sumBy(loans, (loan) => loan.financedBfiFees);

  return {
    count: loans.length,
    commitment: round2(commitment),
    commitmentLessFees: round2(mode === 'direct' ? commitment - financedFees : commitment),
    outstandingBalance: round2(sumBy(loans, (loan) => loan.outstandingBalance)),
    repaidValue: round2(
      sumBy(loans, (loan) => -loan.principalPaymentReceived + loan.ytdInterestPaid),
    ),
    loans,
  };
}

/** Split retained bridge exposure against {@link BRIDGE_RETAINED_CAP}. */
export function allocateBridge(retainedOutstanding: number): BridgeAllocation {
  return {
    retained: round2(retainedOutstanding),
    capped: round2(Math.min(retainedOutstanding, BRIDGE_RETAINED_CAP)),
    excess: round2(Math.max(0, retainedOutstanding - BRIDGE_RETAINED_CAP)),
    cap: BRIDGE_RETAINED_CAP,
  };
}

/**
 * Build one Guarantor Summary row.
 *
 * ASSUMED: `bll` is the borrower's single limit, repeated on every category row. The
 * workbook computes one limit per borrower and the mockup shows the same figure on all
 * four rows, but whether the limit is genuinely shared across categories or should be
 * derived per category is an open business question. If it becomes per-category, this
 * is the one place that changes.
 */
function buildCategory(
  category: LendingCategory,
  bll: number,
  outstanding: number,
): CategoryResult {
  const regulatoryCapacity = round2(Math.max(0, bll - outstanding));

  return {
    category,
    bll: round2(bll),
    outstanding: round2(outstanding),
    surplus: round2(Math.max(0, outstanding - bll)),
    regulatoryCapacity,
    maxSurplus: MAX_SURPLUS_PER_CATEGORY,
    total: round2(regulatoryCapacity + MAX_SURPLUS_PER_CATEGORY),
  };
}

/**
 * Roll buckets up into the four summary categories.
 *
 * Category exposure is measured as outstanding balance, matching the mockup's column
 * label. Note this is *not* the basis the workbook uses for availability (`B24` works
 * from commitment less fees), so `CategoryResult.regulatoryCapacity` will not agree
 * with {@link BorrowerResult.available}. Both bases are kept on {@link LoanGroup}.
 */
function buildCategories(
  buckets: Record<LoanBucket, BucketResult>,
  bridge: BridgeAllocation,
  bll: number,
): Record<LendingCategory, CategoryResult> {
  const outstandingOf = (bucket: LoanBucket): number =>
    buckets[bucket].outstanding.outstandingBalance;

  const coreOutstanding =
    outstandingOf('construction') +
    outstandingOf('fixFlip') +
    outstandingOf('land') +
    bridge.excess;

  return {
    core: buildCategory('core', bll, coreOutstanding),
    bridge: buildCategory('bridge', bll, bridge.capped),
    loc: buildCategory('loc', bll, outstandingOf('lineOfCredit')),
    dscr: buildCategory('dscr', bll, outstandingOf('rental') + outstandingOf('buildFixRent')),
  };
}

function buildBuckets(
  loans: LoanRecord[],
  mode: BllMode,
): Record<LoanBucket, BucketResult> {
  const byBucket = {} as Record<LoanBucket, BucketResult>;

  for (const bucket of LOAN_BUCKETS) {
    const inBucket = loans.filter((loan) => resolveBucket(loan.loanType) === bucket);
    byBucket[bucket] = {
      bucket,
      outstanding: aggregateLoans(
        inBucket.filter((loan) => loan.isOutstanding),
        mode,
      ),
      repaid: aggregateLoans(
        inBucket.filter((loan) => !loan.isOutstanding),
        mode,
      ),
    };
  }

  return byBucket;
}

/** Split one borrower's ledger into the category-shaped view the UI renders. */
export function computeBreakdown(
  input: BorrowerInput,
  mode: BllMode,
  borrowerLendingLimit: number,
): CategoryBreakdown {
  const buckets = buildBuckets(input.loans, mode);
  const bridge = allocateBridge(buckets.retainedBridge.outstanding.outstandingBalance);

  return {
    buckets,
    bridge,
    categories: buildCategories(buckets, bridge, borrowerLendingLimit),
    externalExperienceCount: input.builtSince2011 + input.gcBonus,
  };
}

/**
 * Merge several borrowers' breakdowns into one, for the guarantor-level view.
 *
 * ASSUMED: entities are additive — buckets concatenate and limits sum. The page only
 * renders when the principal has a single top-level business, so in practice this
 * usually collapses to a single breakdown.
 */
export function combineBreakdowns(results: BorrowerResult[]): CategoryBreakdown {
  if (results.length === 1) {
    return results[0].breakdown;
  }

  const buckets = {} as Record<LoanBucket, BucketResult>;

  for (const bucket of LOAN_BUCKETS) {
    const merged = results.map((result) => result.breakdown.buckets[bucket]);
    buckets[bucket] = {
      bucket,
      outstanding: mergeGroups(merged.map((entry) => entry.outstanding)),
      repaid: mergeGroups(merged.map((entry) => entry.repaid)),
    };
  }

  const bridge = allocateBridge(buckets.retainedBridge.outstanding.outstandingBalance);
  const bll = sumBy(results, (result) => result.borrowerLendingLimit);

  return {
    buckets,
    bridge,
    categories: buildCategories(buckets, bridge, bll),
    externalExperienceCount: sumBy(
      results,
      (result) => result.breakdown.externalExperienceCount,
    ),
  };
}

function mergeGroups(groups: LoanGroup[]): LoanGroup {
  return {
    count: sumBy(groups, (group) => group.count),
    commitment: round2(sumBy(groups, (group) => group.commitment)),
    commitmentLessFees: round2(sumBy(groups, (group) => group.commitmentLessFees)),
    outstandingBalance: round2(sumBy(groups, (group) => group.outstandingBalance)),
    repaidValue: round2(sumBy(groups, (group) => group.repaidValue)),
    loans: groups.flatMap((group) => group.loans),
  };
}

/** An all-zero breakdown, for the no-borrowers case. */
function emptyBreakdown(): CategoryBreakdown {
  const buckets = buildBuckets([], 'direct');
  const bridge = allocateBridge(0);

  return {
    buckets,
    bridge,
    categories: buildCategories(buckets, bridge, 0),
    externalExperienceCount: 0,
  };
}

// ── Per-borrower calc ─────────────────────────────────────────────────────────

/**
 * Compute one borrower's BLL and availability (one `Borrower N` tab → one Summary row).
 */
export function calculateBorrower(input: BorrowerInput): BorrowerResult {
  const mode = resolveMode(input.product);
  const { repaymentCount, score: experienceScore } = computeExperience(input);
  const tier = lookupExperienceTier(experienceScore);

  const outstanding = input.loans.filter((loan) => loan.isOutstanding);

  // Section I totals
  const loanAmount = outstanding.reduce((sum, loan) => sum + loan.commitmentAmount, 0); // B9 / M2
  const financedFees = outstanding.reduce((sum, loan) => sum + loan.financedBfiFees, 0); // E9
  const outstandingBalance = outstanding.reduce(
    (sum, loan) => sum + loan.outstandingBalance,
    0,
  ); // C9 / N2

  // Section III — limit
  const repaymentCredit = computeRepaymentCredit(input.loans); // B18
  const borrowerLendingLimit = Math.min(tier.initialBLL + repaymentCredit, tier.maxBLL); // B19 / J2

  // Section IV — availability & surplus. `BLL − commitment` (B24) is split into its
  // two sides: availability keeps the positive side (L2), surplus the negative side
  // (Summary!P = IF(D−E<0, ABS(D−E), 0)); exactly one of the two is non-zero.
  const commitmentLessFees = mode === 'direct' ? loanAmount - financedFees : loanAmount; // B23 / K2
  const available = Math.max(0, borrowerLendingLimit - commitmentLessFees); // B24 / L2
  const surplus = Math.max(0, commitmentLessFees - borrowerLendingLimit); // Summary!P

  return {
    name: input.name,
    product: input.product,
    mode,
    repaymentCount,
    experienceScore,
    experienceType: tier.type,
    initialLimit: tier.initialBLL,
    repaymentCredit,
    borrowerLendingLimit,
    loanAmount,
    commitmentLessFees,
    outstandingBalance,
    available,
    surplus,
    breakdown: computeBreakdown(input, mode, borrowerLendingLimit),
  };
}

// ── Portfolio summary ─────────────────────────────────────────────────────────

/**
 * Top-level entry point: compute every borrower and the portfolio totals
 * (the Summary tab). Totals are rounded to 2 decimals, matching the workbook.
 */
export function calculateSummary(input: SummaryInput): SummaryResult {
  const borrowers = input.borrowers.map(calculateBorrower);

  const totals: SummaryTotals = {
    borrowerLendingLimit: round2(sumBy(borrowers, (b) => b.borrowerLendingLimit)),
    commitmentLessFees: round2(sumBy(borrowers, (b) => b.commitmentLessFees)),
    loanAmount: round2(sumBy(borrowers, (b) => b.loanAmount)),
    available: round2(sumBy(borrowers, (b) => b.available)),
    outstandingBalance: round2(sumBy(borrowers, (b) => b.outstandingBalance)),
    surplus: round2(sumBy(borrowers, (b) => b.surplus)),
    borrowerCount: borrowers.filter((b) => b.borrowerLendingLimit > 1).length,
  };

  return {
    asOfDate: input.asOfDate,
    borrowers,
    totals,
    surplusLimit: SURPLUS_LIMIT, // Surplus BLL!B12
    surplusRemaining: round2(SURPLUS_LIMIT - totals.surplus), // Surplus BLL!B13
    breakdown: borrowers.length > 0 ? combineBreakdowns(borrowers) : emptyBreakdown(),
  };
}

function sumBy<T>(items: T[], select: (item: T) => number): number {
  return items.reduce((sum, item) => sum + select(item), 0);
}
